import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Col, Input } from "reactstrap";
import Pagination from "../pagination";
import PackageCard from "../packageCard";
import { TableSkeleton } from "../ui/TableSkeleton";
import ModernOrdersList from "./ModernOrdersList";
import Modal from "../modal";
import CreatePackage from "../createPackage";
import DownloadVoucher from "../downloadVoucher";
import CancelPackage from "../cancelPackage";
import FilterModal from "../filterModal";
import useAuth from "../../hooks/useAuth";
import EditOrderDrawer from "../editOrderDrawer/editOrderDrawer";
import DownloadLabelsModal from "components/downloadLabelsModal";
import SearchBar from "../searchBar";
import { PackageOpen, Tags } from "lucide-react";
import "./customTable.scss";

export default function CustomTable({
  title,
  headers,
  data,
  actions,
  totalDocs,
  page,
  completeData,
  updateData,
  docsPerPage,
  loadingTable,
  pageSelected,
  setViewEvidence,
  setPackageEvidence,
  filters,
  setFilters,
  statusDisabled,
  admin,
  // Props para el SearchBar
  showSearchBar = false,
  searchBarProps,
  infiniteScroll = false,
  /**
   * Cuando es true, muestra un CTA inline sobre la tabla para seleccionar todos
   * los pedidos cargados y abrir el modal de etiquetas directo en el paso de
   * opciones con origen "seleccionados" (addAllPackges → DownloadLabelsModal).
   * Se usa en /home al activar la card "Listas para despachar".
   */
  selectAllLabelsCTA = false,
  /**
   * Componente custom de modal de filtros. Default: `FilterModal` (admin/cliente).
   * Para el perfil holding admin se pasa `HoldingFilterModal`, que reemplaza la
   * sección "Asignación" por "Cliente" (Vendedor + Sender ID autocomplete).
   */
  FilterModalComponent = FilterModal,
  /**
   * Node rendered instead of the default empty block (`ct-empty`) when there is
   * no data and `loadingTable` is false. A view uses it to show an error state
   * where "no results" used to be, keeping search/filters above the table
   * accessible. Undefined (default) keeps the previous behavior.
   */
  emptyState,
}) {
  //console.log("CustomTable props:", { data, loadingTable, totalDocs });
  const { user } = useAuth();
  const { t } = useTranslation();
  const [showActions, setShowActions] = useState([]);
  const [checkedPackage, setCheckedPackage] = useState([]);
  const [allChecked, setAllChecked] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState({});
  const [detailModal, setDetailModal] = useState(false);
  const [voucherModal, setVoucherModal] = useState(false);
  const [cancelPackage, setCancelPackage] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [editOrder, setEditOrder] = useState(false);
  // Modal unificado de descarga de etiquetas (seleccionados + pendientes).
  // labelsModalOrigin = 'selected' abre directo en el paso de opciones (CTA de
  // /home); null arranca en el selector de origen.
  const [openLabelsModal, setOpenLabelsModal] = useState(false);
  const [labelsModalOrigin, setLabelsModalOrigin] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const filtersCount = Object.keys(filters).length;

  // Detectar cambios en el tamaño de la ventana
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const actionsData = data.map((el) => {
      return { ...el, actions: false };
    });
    setShowActions(actionsData);
  }, [data]);

  const toggle = (value) => {
    const applyChanges = showActions?.map((el) => {
      if (el.correlative === value?.correlative)
        return { ...el, actions: !el.actions };
      return { ...el };
    });
    setShowActions(applyChanges);
  };

  const addingPackage = (value) => {
    const packageToAdd = completeData?.find(
      (f) => f.followingNumber === value.correlative?.comparative
    );
    const alreadySelected = checkedPackage?.find(
      (f) => f.followingNumber === packageToAdd.followingNumber
    );

    if (!alreadySelected) {
      const newCheckedPackage = [...checkedPackage, packageToAdd];
      setCheckedPackage(newCheckedPackage);
      // Actualizar allChecked basado en si todos están seleccionados
      setAllChecked(newCheckedPackage.length === completeData?.length);
    }

    if (alreadySelected) {
      const newSelected = checkedPackage?.filter(
        (f) => f.followingNumber !== packageToAdd.followingNumber
      );
      setCheckedPackage(newSelected);
      // Si se deselecciona uno, allChecked debe ser false
      setAllChecked(false);
    }
  };

  const addAllPackges = (checked) => {
    if (checked) {
      setCheckedPackage(completeData);
      setAllChecked(true);
    }
    if (!checked) {
      setCheckedPackage([]);
      setAllChecked(false);
    }
  };

  useEffect(() => {
    setCheckedPackage([]);
    setAllChecked(false);
  }, [pageSelected]);

  return (
    <div className="table-container" id="table-container">
      <Modal
        open={detailModal}
        setOpen={() => setDetailModal()}
        body={
          <CreatePackage
            edit
            defaultData={completeData?.find(
              (f) =>
                f.followingNumber === selectedPackage.correlative?.comparative
            )}
          />
        }
      />
      <Modal
        open={voucherModal}
        setOpen={() => setVoucherModal()}
        bodyClassName="package-detail-modal-wrapper"
        body={
          <DownloadVoucher
            defaultData={completeData?.find(
              (f) =>
                f.followingNumber === selectedPackage.correlative?.comparative
            )}
            existingReturnLabel={(() => {
              const parent = completeData?.find(
                (f) => f.followingNumber === selectedPackage.correlative?.comparative
              );
              return completeData?.find(
                (f) => parent?._id && String(f.relatedTo) === String(parent._id)
              );
            })()}
          />
        }
      />
      <CancelPackage
        isOpen={cancelPackage}
        closeModal={setCancelPackage}
        defaultData={completeData?.find(
          (f) => f.followingNumber === selectedPackage.correlative?.comparative
        )}
        updateData={updateData}
      />
      <DownloadLabelsModal
        isOpen={openLabelsModal}
        onClose={() => setOpenLabelsModal(false)}
        packages={checkedPackage}
        statusDisabled={statusDisabled}
        initialOrigin={labelsModalOrigin}
      />

      <FilterModalComponent
        open={openFilters}
        activeFilters={filters}
        callback={setFilters}
        closeModal={setOpenFilters}
        statusDisabled={statusDisabled}
        user={user}
      />
 
      <EditOrderDrawer
        isOpen={editOrder}
        closeModal={setEditOrder}
        data={completeData?.find(
          (f) => f.followingNumber === selectedPackage.correlative?.comparative
        )}
        updateData={updateData}
      />
      {/* CTA inline: seleccionar todos los pedidos cargados y abrir el modal de
          etiquetas directo en el paso de opciones (origen: seleccionados). */}
      {selectAllLabelsCTA && !loadingTable && completeData?.length > 0 && (
        <div className="ct-bulk-cta">
          <span className="ct-bulk-cta__info">
            <span className="ct-bulk-cta__icon material-symbols-outlined" aria-hidden="true">
              qr_code_2
            </span>
            <span className="ct-bulk-cta__text">
              {t("Tenés pedidos listos para despachar.")}
            </span>
          </span>
          <button
            type="button"
            className="ct-bulk-cta__btn"
            onClick={() => {
              addAllPackges(true);
              setLabelsModalOrigin("selected");
              setOpenLabelsModal(true);
            }}
          >
            {t("Seleccionar todo y descargar etiquetas ({{count}})", {
              count: completeData.length,
            })}
          </button>
        </div>
      )}

      {/* Header de la tabla con acciones */}
      <div className="table-header-actions">
        <div className="table-header-actions__title-section">
          <h3 className="table-header-actions__title hide-mobile">{title}</h3>
          {!loadingTable && (
            <span className="table-header-actions__counter">
              {infiniteScroll ? (
                t("Mostrando {{shown}} de {{total}} pedidos", {
                  shown: completeData?.length || data?.length || 0,
                  total: totalDocs,
                })
              ) : (
                t("Mostrando {{from}} - {{to}} de {{total}} pedidos", {
                  from: docsPerPage * (pageSelected + 1) - (docsPerPage - 1),
                  to:
                    docsPerPage * (pageSelected + 1) > totalDocs
                      ? totalDocs
                      : docsPerPage * (pageSelected + 1),
                  total: totalDocs,
                })
              )}
            </span>
          )}
        </div>
        
        {/* SearchBar integrado en la tabla */}
        {showSearchBar && searchBarProps && (
          <div className="table-header-actions__search">
            <SearchBar {...searchBarProps} showSpinner={false} />
          </div>
        )}
        
        <div className="table-header-actions__buttons">
          {/* Etiquetas: modal unificado (seleccionados + pendientes masiva) */}
          <button
            className="table-action-btn"
            onClick={() => {
              setLabelsModalOrigin(null);
              setOpenLabelsModal(true);
            }}
            title={t("Descargar etiquetas")}
          >
            <Tags className="table-action-btn__icon" aria-hidden="true" />
            <span className="table-action-btn__text">
              <span className="hide-mobile">{t("Etiquetas")}</span>
              {checkedPackage?.length > 0 && (
                <span className="filter-badge">{checkedPackage.length}</span>
              )}
            </span>
          </button>


          {/* Más filtros (panel lateral) */}
          <button
            className="table-action-btn table-action-btn--filter"
            onClick={() => setOpenFilters(true)}
            title={t("Más filtros")}
          >
            <svg className="table-action-btn__icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 17v2h6v-2H3zM3 5v2h10V5H3zm10 16v-2h8v-2h-8v-2h-2v6h2zM7 9v2H3v2h4v2h2V9H7zm14 4v-2H11v2h10zm-6-4h2V7h4V5h-4V3h-2v6z"/>
            </svg>
            <span className="table-action-btn__text">
              <span className="hide-mobile">{t("Más filtros")}</span>
              {filtersCount > 0 && (
                <span className="filter-badge">{filtersCount}</span>
              )}
            </span>
          </button>
        </div>
      </div>
      <Col
        xs="12"
        md="12"
        xl="12"
        className="d-flex justify-content-center custom-table-data w-100"
        style={{ minHeight: (!loadingTable && (!data || data.length === 0)) ? 'auto' : `${docsPerPage * 55}px` }}
      >
        {/*console.log("Renderizando CustomTable:", { loadingTable, dataLength: data?.length, data: data })*/}
        {loadingTable ? (
          // Skeleton de filas (no spinner bloqueante) durante búsqueda/filtro/scroll.
          // Columnas derivadas de los headers reales + checkbox + acciones; filas
          // acotadas a docsPerPage para no exceder el alto de la vista.
          <TableSkeleton
            className="w-100"
            rows={Math.min(docsPerPage || 8, 10)}
            columns={(headers?.length || 6) + 1}
          />
        ) : !data || data.length === 0 ? (
          // A view-provided empty state (e.g. an error) replaces the default
          // "no packages" block; without it the block renders as before.
          emptyState !== undefined && emptyState !== null ? (
            emptyState
          ) : (
            <div className="ct-empty" role="status">
              <div className="ct-empty__icon">
                <PackageOpen size={30} strokeWidth={1.75} />
              </div>
              <p className="ct-empty__title">{t("No hay paquetes para mostrar")}</p>
              <span className="ct-empty__hint">
                {t("Ajusta los filtros o la búsqueda para ver resultados.")}
              </span>
            </div>
          )
        ) : isMobile ? (
          // Vista de cards para mobile
          <div className="w-100 mobile-cards-container">
            {/* Checkbox para seleccionar todos */}
            <div className="mobile-select-all">
              <Input
                type="checkbox"
                className="checkbox-custom-style"
                onClick={(e) => addAllPackges(e.target.checked)}
                onChange={(e) => setAllChecked(e.target.checked)}
                checked={allChecked}
              />
              <span className="ml-2">{t("Seleccionar todos")}</span>
            </div>
            
            {/* Lista de cards */}
            {data?.map((item, index) => (
              <PackageCard
                key={index}
                packageData={item}
                isChecked={checkedPackage?.find(
                  (f) => f.followingNumber === item.correlative?.comparative
                )}
                onCheckboxChange={addingPackage}
                onViewEvidence={() => {
                  setPackageEvidence(item);
                  setViewEvidence(true);
                }}
                onViewVoucher={() => {
                  setSelectedPackage(item);
                  setVoucherModal(true);
                }}
                onCancel={() => {
                  setSelectedPackage(item);
                  setCancelPackage(true);
                }}
                onEdit={() => {
                  setSelectedPackage(item);
                  setEditOrder(true);
                }}
                completeData={completeData}
              />
            ))}
          </div>
        ) : (
          // Vista de lista moderna para desktop (sin scroll lateral)
          <ModernOrdersList
            data={data}
            completeData={completeData}
            checkedPackage={checkedPackage}
            addingPackage={addingPackage}
            allChecked={allChecked}
            addAllPackges={addAllPackges}
            showActions={showActions}
            toggle={toggle}
            setSelectedPackage={setSelectedPackage}
            setVoucherModal={setVoucherModal}
            setCancelPackage={setCancelPackage}
            setEditOrder={setEditOrder}
            setPackageEvidence={setPackageEvidence}
            setViewEvidence={setViewEvidence}
            user={user}
          />
        )}
        {/* Tabla tradicional comentada - ahora usamos ModernOrdersList
        {!isMobile && (
          <Table className="w-100" responsive>
            <thead className="table-container__table-headers">
              <tr>
                <th className="table-container__table-headers__header text-center">
                  <span>
                    <Input
                      type="checkbox"
                      className="clickable-correlative checkbox-custom-style"
                      onClick={(e) => addAllPackges(e.target.checked)}
                      onChange={(e) => setAllChecked(e.target.checked)}
                      checked={allChecked}
                    />
                  </span>
                </th>
                {headers?.map((header, index) => (
                  <th
                    key={index}
                    className="table-container__table-headers__header"
                  >
                    <span>{header.label || header}</span>

                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.map((item, index) => {
                const createData = Object.values(item);
                const isDropdownOpen = showActions?.find(
                  (el) =>
                    el.correlative?.comparative ===
                    item?.correlative?.comparative
                )?.actions;
                return (
                  <tr 
                    key={index} 
                    className={`table-container__table-data ${isDropdownOpen ? 'has-dropdown-open' : ''}`}
                  >
                    <td className="align-items-center h-100 text-center custom-table-checkbox">
                      <span>
                        <Input
                          type="checkbox"
                          className="clickable-correlative checkbox-custom-style"
                          onClick={() => addingPackage(item)}
                          checked={checkedPackage?.find(
                            (f) =>
                              f.followingNumber === item.correlative?.comparative
                          )}
                        />
                      </span>
                    </td>
                    {createData?.map((i, v) => {
                      if (i?.type === "correlative") {
                        return (
                          <td
                            className="clickable-correlative align-items-center h-100"
                            key={v}
                            onClick={() => {
                              setPackageEvidence(item);
                              setViewEvidence(true);
                            }}
                            onKeyDown={() => {
                              setPackageEvidence(item);
                              setViewEvidence(true);
                            }}
                          >
                            {i?.value}
                          </td>
                        );
                      }
                      if (i?.name) {
                        return (
                          <td
                            key={v}
                            className="truncate-text align-items-center h-100"
                          >
                            <div className="custom-center-inner-td w-36">
                              <span className="truncate-text flex items-center gap-1">
                                <img
                                  src={
                                    i?.icon ? i?.icon : iconList["defaultAvatar"]
                                  }
                                  className="table-container__table-data__delivery-icon dark-safe-img"
                                  alt="icon"
                                />
                                {i?.name}
                              </span>
                            </div>
                          </td>
                        );
                      }
                      if (i?.statusPill) {
                        return (
                          <td
                            key={v}
                            className="td-custom-status-pill truncate-text align-items-center h-100"
                          >
                            <span className="text-truncate w-100">
                              {i?.statusPill}
                            </span>
                          </td>
                        );
                      }
                      return (
                        <td key={v} className="td-custom-width truncate-text">
                          <span className="text-truncate">{i}</span>
                        </td>
                      );
                    })}
                    {actions && (
                      <td className="td-custom-actions">
                        <div className="custom-center-inner-td">
                          {user?.role === roleRoutes.client ? (
                            <Dropdown
                              isOpen={
                                showActions?.find(
                                  (el) =>
                                    el.correlative?.comparative ===
                                    item?.correlative?.comparative
                                )?.actions
                              }
                              toggle={() => toggle(item)}
                              direction="down"
                              data-toggle="dropdown"
                              tag="div"
                              className="dropdown-new-style"
                            >
                              <DropdownToggle>
                                <img src={iconList["actions"]} alt="actions" />
                              </DropdownToggle>
                              <DropdownMenu className="custom-dropdown-menu-opaque">
                                <DropdownItem
                                  className="dropdown-font mb-1"
                                  onClick={() => {
                                    setSelectedPackage(item);
                                    setVoucherModal(true);
                                  }}
                                >
                                  <div className="flex items-center">
                                    <img
                                      className="dropdown-font__image mr-2"
                                      alt="detail"
                                      src={iconList.qrcode}
                                    />{" "}
                                    Ver detalle
                                  </div>
                                </DropdownItem>
                                <Divider />
                                <DropdownItem
                                  className="dropdown-font mt-1"
                                  disabled={item?.status?.status !== 0 && item?.status?.status !== 100}
                                  onClick={() => {
                                    setSelectedPackage(item);
                                    setCancelPackage(true);
                                  }}
                                >

                                  <div className="flex items-center">
                                    <img
                                      className="dropdown-font__image mr-2"
                                      alt="cancel"
                                      src={iconList.close}
                                    />{" "}
                                    Cancelar
                                  </div>
                                </DropdownItem>
                              </DropdownMenu>
                            </Dropdown>
                          ) : (
                            <Dropdown
                              isOpen={
                                showActions?.find(
                                  (el) =>
                                    el.correlative?.comparative ===
                                    item?.correlative?.comparative
                                )?.actions
                              }
                              toggle={() => toggle(item)}
                              direction="down"
                              data-toggle="dropdown"
                              tag="div"
                              className="dropdown-new-style"
                            >
                              <DropdownToggle>
                                <img src={iconList["actions"]} alt="actions" />
                              </DropdownToggle>
                              <DropdownMenu className="custom-dropdown-menu-opaque">
                                <DropdownItem
                                  className="dropdown-font mb-1"
                                  onClick={() => {
                                    setSelectedPackage(item);
                                    setVoucherModal(true);
                                  }}
                                >
                                  <div className="flex items-center gap-1">
                                    <img
                                      className="dropdown-font__image mr-2"
                                      alt="detail"
                                      src={iconList.qrcode}
                                    />{" "}
                                    Ver etiqueta
                                  </div>
                                </DropdownItem>
                                <Divider />
                                <DropdownItem
                                  className="dropdown-font mt-1"
                                  disabled={
                                    item?.status?.value === 4 ||
                                    item?.status?.value === 5
                                  }
                                  onClick={() => {
                                    setSelectedPackage(item);
                                    setAssignDelivery(true);
                                  }}
                                >
                                  <div className="flex items-center gap-1">
                                    <img
                                      className="dropdown-font__image mr-2"
                                      alt="cancel"
                                      src={iconList.manageAccount}
                                    />{" "}
                                    Asignar repartidor
                                  </div>
                                </DropdownItem>
                                <Divider />
                                <DropdownItem
                                  className="dropdown-font mt-1"
                                  disabled={
                                    item?.status?.value === 4 ||
                                    item?.status?.value === 5
                                  }
                                  onClick={() => {
                                    setSelectedPackage(item);
                                    setChangeStatusModal(true);
                                  }}
                                >
                                  <div className="flex items-center gap-1">
                                    <img
                                      className="dropdown-font__image mr-2"
                                      alt="cancel"
                                      src={iconList.edit}
                                    />{" "}
                                    Cambiar estado
                                  </div>
                                </DropdownItem>
                                <Divider />
                                <DropdownItem
                                  className="dropdown-font mt-1"
                                  disabled={item?.status?.value !== 0}
                                  onClick={() => {
                                    setSelectedPackage(item);
                                    setCancelPackage(true);
                                  }}
                                >
                                  <div className="flex items-center gap-1">
                                    <img
                                      className="dropdown-font__image mr-2"
                                      alt="cancel"
                                      src={iconList.close}
                                    />{" "}
                                    Cancelar
                                  </div>
                                </DropdownItem>
                              </DropdownMenu>
                            </Dropdown>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )} */}
      </Col>
      {!infiniteScroll && (
        <div className="table-footer-container">
          {!loadingTable ? (
            <>
              <Pagination
                documents={totalDocs || 1}
                pagination={docsPerPage || 0}
                page={page || 1}
                pageSelected={pageSelected}
              />
            </>
          ) : (
            <div className="table-counter">Cargando...</div>
          )}
        </div>
      )}
    </div>
  );
}
